import { Markdown } from "@tiptap/markdown";

export type Alignment = "left" | "center" | "right";

export interface MarkdownImageAttributes {
  src?: string;
  alt?: string | null;
  title?: string | null;
  width?: string | null;
  height?: string | null;
  alignment?: Alignment;
  style?: string | null;
}

export const ALIGNMENT_VALUES: Alignment[] = ["left", "center", "right"];

const escapeAttribute = (value?: string | null) => {
  if (!value) {
    return "";
  }

  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
};

const buildAttributeString = (attributes: Record<string, string | null | undefined>) => {
  return Object.entries(attributes)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) => `${key}="${escapeAttribute(value)}"`)
    .join(" ");
};

const normalizeDimension = (value?: string | null) => {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^\d+$/.test(trimmed)) {
    return `${trimmed}px`;
  }

  return trimmed;
};

const formatDimensionAttribute = (value?: string | null) => {
  if (!value) return null;

  const match = value.trim().match(/^(\d+)(px)?$/);
  if (!match) return null;

  return match[1];
};

export const extractAlignmentFromClassList = (classNames?: string | null): Alignment | null => {
  if (!classNames) return null;

  const match = classNames.match(/align-(left|center|right)/);
  return match ? (match[1] as Alignment) : null;
};

export const extractAlignmentFromStyle = (style?: string | null): Alignment | null => {
  if (!style) return null;

  const match = style.match(/text-align\s*:\s*(left|center|right)/i);
  return match ? (match[1].toLowerCase() as Alignment) : null;
};

const resolveAlignment = (
  options: { dataAlign?: string | null; classNames?: string | null; style?: string | null },
  fallback: Alignment = "left",
): Alignment => {
  const dataAlign = options.dataAlign?.toLowerCase();

  if (dataAlign && ALIGNMENT_VALUES.includes(dataAlign as Alignment)) {
    return dataAlign as Alignment;
  }

  const classAlign = extractAlignmentFromClassList(options.classNames);
  if (classAlign) {
    return classAlign;
  }

  const styleAlign = extractAlignmentFromStyle(options.style);
  if (styleAlign) {
    return styleAlign;
  }

  return fallback;
};

const extractStyleDimension = (style: string | null | undefined, property: "width" | "height") => {
  if (!style) return null;

  const regex = new RegExp(`${property}\\s*:\\s*([^;]+)`, "i");
  const match = style.match(regex);

  return match ? match[1].trim() : null;
};

const composeImageStyle = (baseStyle: string | null | undefined, width?: string | null, height?: string | null) => {
  const declarations: string[] = [];

  if (baseStyle?.trim()) {
    declarations.push(baseStyle.trim().replace(/;+\s*$/, ";"));
  }

  if (width && !extractStyleDimension(baseStyle, "width")) {
    declarations.push(`width: ${width};`);
  }

  if (height && !extractStyleDimension(baseStyle, "height")) {
    declarations.push(`height: ${height};`);
  }
  declarations.push(`display: inline;`);

  if (!declarations.length) {
    return undefined;
  }

  return declarations.join(" ").trim();
};

export const serializeImageNodeToHtml = (attrs: MarkdownImageAttributes) => {
  const { src, alt, title, width, height, alignment = "left", style } = attrs;
  const safeAlignment: Alignment = ALIGNMENT_VALUES.includes(alignment) ? alignment : "left";
  const mergedStyle = composeImageStyle(style, width, height);

  const wrapperAttributes = buildAttributeString({
    class: `markdown-image-wrapper align-${safeAlignment}`,
    "data-align": safeAlignment,
    style: safeAlignment !== "left" ? `text-align: ${safeAlignment};` : undefined,
  });

  const containerAttributes = buildAttributeString({
    class: `markdown-image-container align-${safeAlignment}`,
  });
  console.log("mergedStyle:", mergedStyle);
  const imageAttributes = buildAttributeString({
    class: `markdown-image align-${safeAlignment}`,
    src,
    alt: typeof alt === "string" ? alt : undefined,
    title,
    width: formatDimensionAttribute(width),
    height: formatDimensionAttribute(height),
    style: mergedStyle,
  });

  const wrapperOpenTag = wrapperAttributes ? `<div ${wrapperAttributes}>` : "<div>";
  const containerOpenTag = containerAttributes ? `<div ${containerAttributes}>` : "<div>";
  const imageTag = imageAttributes ? `<img ${imageAttributes} />` : "<img />";

  return `${wrapperOpenTag}${containerOpenTag}${imageTag}</div></div>`;
};

const isSupportedHtmlImageNode = (node: ChildNode): boolean => {
  if (node.nodeType === Node.TEXT_NODE) {
    return !(node.textContent || "").trim();
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }

  const element = node as Element;
  const tag = element.tagName.toLowerCase();

  if (tag === "img") {
    return true;
  }

  if (["div", "span", "figure", "p"].includes(tag)) {
    return Array.from(element.childNodes).every(isSupportedHtmlImageNode);
  }

  return false;
};

const canProcessHtmlFragment = (fragment: DocumentFragment) => {
  return Array.from(fragment.childNodes).every(isSupportedHtmlImageNode);
};

const parseHtmlImageToken = (token: any, state: any) => {
  if (typeof document === "undefined") {
    return false;
  }

  const rawHtml = (token.text || token.raw || "").trim();
  if (!rawHtml || !rawHtml.includes("<img")) {
    return false;
  }

  const template = document.createElement("template");
  template.innerHTML = rawHtml;

  if (!canProcessHtmlFragment(template.content)) {
    return false;
  }

  const imageElements = Array.from(template.content.querySelectorAll("img"));
  if (imageElements.length === 0) {
    return false;
  }

  let parsedAtLeastOne = false;

  imageElements.forEach((image) => {
    const src = image.getAttribute("src");
    if (!src) return;

    const wrapper = image.closest(".markdown-image-wrapper");

    const width =
      normalizeDimension(image.getAttribute("width")) ||
      normalizeDimension(extractStyleDimension(image.getAttribute("style"), "width"));
    const height =
      normalizeDimension(image.getAttribute("height")) ||
      normalizeDimension(extractStyleDimension(image.getAttribute("style"), "height"));

    const alignment = resolveAlignment(
      {
        dataAlign: wrapper?.getAttribute("data-align") || image.getAttribute("data-align"),
        classNames: wrapper?.getAttribute("class") || image.getAttribute("class"),
        style: wrapper?.getAttribute("style"),
      },
      "left",
    );

    state.openNode("image", {
      src,
      alt: image.getAttribute("alt") || "",
      title: image.getAttribute("title"),
      width,
      height,
      alignment,
      style: image.getAttribute("style"),
    });
    state.closeNode();
    parsedAtLeastOne = true;
  });

  return parsedAtLeastOne;
};

const sanitizeTitleAndExtractAttributes = (title?: string | null) => {
  if (!title) {
    return { title: null, width: null, height: null, alignment: "left" as Alignment };
  }

  let cleanedTitle = title;
  let width: string | null = null;
  let height: string | null = null;
  let alignment: Alignment = "left";

  if (title.includes("{") && title.includes("}")) {
    const attrMatch = title.match(/\{([^}]+)\}/);

    if (attrMatch) {
      const attrs = attrMatch[1];
      const widthMatch = attrs.match(/width=(\d+)/);
      const heightMatch = attrs.match(/height=(\d+)/);
      const alignMatch = attrs.match(/align=(left|center|right)/);

      if (widthMatch) width = `${widthMatch[1]}px`;
      if (heightMatch) height = `${heightMatch[1]}px`;
      if (alignMatch) alignment = alignMatch[1] as Alignment;

      cleanedTitle = title.replace(/\{[^}]+\}/, "").trim() || null;
    }
  }

  return {
    title: cleanedTitle,
    width,
    height,
    alignment,
  };
};

// Custom markdown serializer for images with attributes
export const MarkdownImageSerializer = Markdown.extend({
  addStorage() {
    return {
      ...this.parent?.(),
      markdown: {
        ...this.parent?.()?.markdown,
        serializers: {
          ...this.parent?.()?.markdown?.serializers,
          image: (state: any, node: any) => {
            state.write(serializeImageNodeToHtml(node.attrs || {}));
          },
        },
        parsers: {
          ...this.parent?.()?.markdown?.parsers,
          // Custom parser for image attributes and HTML wrappers
          image: (state: any, token: any) => {
            const src = token.attrGet("src");
            const alt = token.content;
            const attributes = sanitizeTitleAndExtractAttributes(token.attrGet("title"));

            state.openNode("image", {
              src,
              alt,
              title: attributes.title,
              width: attributes.width,
              height: attributes.height,
              alignment: attributes.alignment,
            });
            state.closeNode();
          },
          html: (state: any, token: any) => {
            const parsed = parseHtmlImageToken(token, state);
            if (!parsed) {
              this.parent?.()?.markdown?.parsers?.html?.(state, token);
            }
          },
        },
      },
    };
  },
});
